import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useOddsFormat } from '~SpectateStore/hooks/userHooks';
import { decimalPriceOdds } from '~shared/utils/selections';
import formatCurrency from '~shared/utils/currencyUtils';
import { getI18nBetErrorKey } from '~Betslip/utils/errorMappings';
import Banners from '~Betslip/uiModes/Full/Components/Banners';

import { eCommerceGTMPurchase } from '~shared/utils/google-tag-manager';

const ReceiptSingleBet = ({ bet }) => {
    const [ t ] = useTranslation();
    const [collapsed, setCollapsed] = useState(true);

    const {
        success,
        id,
        selection_name,
        event_name,
        decimal_price,
        fraction_price,
        american_price,
        stake,
        total_stake,
        potential_returns,
        bet_type,
        selections,
        each_way,
        free_bet_id,
        selectionId,
    } = bet;

    const castMarkets = ['forecast', 'tricast' ];
    const betType = bet_type.toLowerCase();
    const isCastMarket = castMarkets.includes(betType);
    const betTypesAllowed = ['singles', 'scorecast', ...castMarkets];
    const isMulti = !(betTypesAllowed.includes(betType));
    const oddsFormat = useOddsFormat();
    const isSp = !decimal_price || decimal_price === '0';

    useEffect(() => {
        const castmarketId = isCastMarket ? selectionId : '';
        let prices = {};
        let selectionDetails = {};

        if (isMulti) {
            selections.forEach(selection => {
                prices[selection.id] = {
                    decimal: selection.decimal_price,
                };

                selectionDetails[selection.id] = {
                    ...selection,
                };
            });
        } else {
            prices = {
                [selectionId]: {
                    decimal: decimal_price,
                },
            };

            selectionDetails = {
                [selectionId]: bet,
            };
        }

        eCommerceGTMPurchase(bet, prices, selectionDetails, castmarketId);
    }, []);

    const formattedOdds = (prices) => {
        if (!prices.decimal_price || Number(prices.decimal_price) === 0) {
            return t('betslip.sp');
        }

        switch (oddsFormat) {
            case 'AMERICAN':
                return prices.american_price;
            case 'FRACTION':
                return prices.fraction_price;
            default:
                return decimalPriceOdds(prices.decimal_price);
        }
    };

    const selectionDetailsClass = classNames('fullslip__receipt-selections-details', {
        'fullslip__receipt-selections-details--collapsed': collapsed,
    });

    const dropdownIconClass = classNames('fullslip__receipt-dropdown', {
        'fullslip__receipt-dropdown--flipped': !collapsed,
    });

    const headerTitle = () => {
        if (isMulti) {
            return (
                <>
                    {t(`bet_types.name.${bet_type}`)}
                    <span className="fullslip__receipt-selection-count">
                        ({t('betslip.selections_count', { count: bet.selections.length })})
                    </span>
                </>
            );
        }

        return selection_name;
    };

    const selectionDetails = (selection) => {
        return (
            <div key={selection.selection_name}>
                {selection.selection_name}
                <span className="fullslip__receipt-odds" dir="auto">
                    {formattedOdds({
                        decimal_price: selection.decimal_price,
                        fraction_price: selection.fraction_price,
                        american_price: selection.american_price,
                    })}
                </span>
                {selection.event_name}
            </div>
        );
    };

    const headerSection = () => {
        return (
            <div
                className="fullslip__receipt-bet-header"
                onClick={() => setCollapsed(!collapsed)}
            >
                <span className="fullslip__receipt-bet-name-container">
                    <span className="fullslip__receipt-bet-name" dir="auto">{headerTitle()}</span>
                    <span className={dropdownIconClass}></span>
                </span>
                {
                    success
                    ? <span className="fullslip__receipt-bet-placed">{t('betslip.bet_placed_success')}</span>
                    : <span className="fullslip__receipt-bet-declined">{t('betslip.bet_placed_fail')}</span>
                }
            </div>
        );
    };

    const selectionName = () => {
        if (isCastMarket) {
            return bet.selection_names.map((name, idx) => {
                const place = t(`betslip.fctc_header_${idx + 1}`);

                return (
                    <>
                        {name} <span className="fullslip__fctc-place">{place}, </span>
                    </>
                );
            });
        }

        return selection_name;
    };

    const selectionDetailsSection = () => {
        return (
            <>
                <div className="fullslip__receipt-bet-id">{t('betslip.bet_id')}: {id}</div>
                <div className="fullslip__receipt-stake-container" dir="auto">
                    <p className="fullslip__receipt-stake-row">
                        <span>{t('betslip.stake')}: </span>
                        <span className="fullslip__receipt-stake-value" dir="auto">{formatCurrency(stake)} </span>
                        {
                            each_way
                            ?
                                <span className="fullslip__receipt-ew">
                                    {t('betslip.ew')} ({t('betslip.total_stake')} <span className="fullslip__receipt-stake-value" dir="auto">{formatCurrency(total_stake)}</span>)
                                </span>
                            : null
                        }
                        {
                            free_bet_id
                            ? <span className="fullslip__receipt-freebet"></span>
                            : null
                        }
                    </p>
                    <span>{t('betslip.potential_returns')}: </span>
                    <span dir="auto" className="fullslip__receipt-return-value">
                        {
                            (potential_returns)
                            ? formatCurrency(potential_returns)
                            : t('betslip.na')
                        }
                    </span>
                </div>
            </>
        );
    };

    const betFailedSection = () => {
        const {
            id,
            info,
        } = bet.error;

        const {
            minimum_odds_allowed: minOdds,
            stake_allowed: stakeAllowed,
        } = info || {}; // if null or undefined

        const param = minOdds || formatCurrency(stakeAllowed);

        const msg = t(`betslip.${getI18nBetErrorKey(id)}`, { param });

        return (
            <p className="fullslip__receipt-error-msg">{msg}</p>
        );
    };


    return (
        <div className="fullslip__receipt-bet">
            <Banners selectionDetails={bet} isSp={isSp} />
            {headerSection()}
            <div className={selectionDetailsClass}>
                {
                    isMulti
                    ?
                        selections.map(selection => {
                            return selectionDetails(selection);
                        })
                    :
                        selectionDetails({
                            selection_name: selectionName(),
                            event_name,
                            decimal_price,
                            fraction_price,
                            american_price,
                        })
                }
                {
                    success
                    ? selectionDetailsSection()
                    : null
                }
            </div>
            {
                !success
                ? betFailedSection()
                : null
            }
        </div>
    );
};

ReceiptSingleBet.propTypes = {
    bet: PropTypes.object,
};

export default ReceiptSingleBet;
